import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useMarketingContent } from '../hooks/useMarketingContent';
import { 
  Smartphone, 
  Target, 
  Users, 
  Heart, 
  Zap, 
  Globe,
  Award,
  Lightbulb,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Menu,
  X
} from 'lucide-react';

const AboutPage = () => {
  const { t } = useTranslation();
  const { content: cmsContent } = useMarketingContent();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const team = [
    {
      name: 'Andrew Curtis',
      role: 'Founder & CEO',
      bio: 'Passionate about bridging the physical and digital worlds through innovative technology.',
      gradient: 'from-indigo-400 to-purple-500'
    },
    {
      name: 'Tech Team',
      role: 'Development',
      bio: 'A dedicated team of engineers building scalable, user-friendly solutions.',
      gradient: 'from-blue-400 to-cyan-500'
    },
    {
      name: 'Support Team',
      role: 'Customer Success',
      bio: 'Always here to help you get the most out of ScanIt2Go.',
      gradient: 'from-green-400 to-emerald-500'
    }
  ];

  const values = [
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: 'Innovation',
      description: 'We constantly push boundaries to create new ways of connecting physical objects to digital experiences.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Customer Focus',
      description: 'Every feature we build starts with understanding our customers\' needs and challenges.'
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Passion',
      description: 'We genuinely love what we do and it shows in every interaction and product we create.'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Simplicity',
      description: 'Complex technology should be simple to use. We make NFC accessible to everyone.'
    }
  ];

  const milestones = [
    { year: '2023', event: 'ScanIt2Go founded with a vision to democratize NFC technology' },
    { year: '2024', event: 'Launched B2B platform with white-label capabilities' },
    { year: '2024', event: 'Introduced Gifts & Awards product line for consumers' },
    { year: '2025', event: 'Expanded to serve 500+ businesses across 180+ countries' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">ScanIt2Go</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.features')}</Link>
              <Link to="/#solutions" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.solutions')}</Link>
              <Link to="/#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.pricing')}</Link>
              <Link to="/about" className="text-indigo-600 font-medium">{t('marketing.nav.about')}</Link>
              <Link to="/faq" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.faq')}</Link>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.login')}</Link>
              <Link to="/#contact" className="px-5 py-2.5 bg-indigo-600 text-white rounded-full font-medium hover:bg-indigo-700 transition-colors">
                {t('marketing.nav.getStarted')}
              </Link>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="px-6 py-4 space-y-4">
              <Link to="/#features" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.features')}</Link>
              <Link to="/#solutions" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.solutions')}</Link>
              <Link to="/#pricing" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.pricing')}</Link>
              <Link to="/about" className="block text-indigo-600 font-medium">{t('marketing.nav.about')}</Link>
              <Link to="/faq" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.faq')}</Link>
              <hr className="border-gray-100" />
              <Link to="/admin" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.login')}</Link>
              <Link to="/#contact" className="block w-full text-center px-5 py-2.5 bg-indigo-600 text-white rounded-full font-medium">
                {t('marketing.nav.getStarted')}
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t('about.badge')}
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              {cmsContent.about_hero?.title || t('about.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              {cmsContent.about_hero?.subtitle || t('about.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('about.ourMission')}</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('about.missionText')}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 font-medium">
                  500+ {t('about.stats.businessesServed')}
                </div>
                <div className="px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 font-medium">
                  180+ {t('about.stats.countriesReached')}
                </div>
                <div className="px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 font-medium">
                  99.9% {t('about.stats.uptime')}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl p-8">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Our Vision</h3>
                      <p className="text-gray-500">A world where every object tells a story</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Make NFC technology accessible to everyone',
                      'Create memorable experiences through innovation',
                      'Help businesses connect with customers',
                      'Turn moments into lasting memories'
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-700">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.ourValues')}</h2>
            <p className="text-xl text-gray-600">{t('about.visionText')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Lightbulb className="w-6 h-6" />, key: 'innovation' },
              { icon: <Users className="w-6 h-6" />, key: 'customerFocus' },
              { icon: <Heart className="w-6 h-6" />, key: 'passion' },
              { icon: <Zap className="w-6 h-6" />, key: 'simplicity' }
            ].map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t(`about.values.${value.key}`)}</h3>
                <p className="text-gray-600">{t(`about.values.${value.key}Desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.ourJourney')}</h2>
            <p className="text-xl text-gray-600">{t('about.subtitle')}</p>
          </div>

          <div className="space-y-8">
            {[
              { year: t('about.journey.year2023'), event: t('about.journey.desc2023') },
              { year: t('about.journey.year2024'), event: t('about.journey.desc2024') },
              { year: t('about.journey.year2025'), event: t('about.journey.desc2025') }
            ].map((milestone, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-start gap-6"
              >
                <div className="w-20 flex-shrink-0">
                  <span className="text-2xl font-bold text-indigo-600">{milestone.year}</span>
                </div>
                <div className="flex-1 bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                  <p className="text-gray-700">{milestone.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.team.title')}</h2>
            <p className="text-xl text-gray-600">{t('about.team.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {team.map((member, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center border border-gray-100 hover:shadow-xl transition-all"
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${member.gradient} rounded-full mx-auto mb-6`} />
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-indigo-600 font-medium mb-4">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-12 text-center text-white"
          >
            <h2 className="text-4xl font-bold mb-4">{t('about.ctaTitle')}</h2>
            <p className="text-xl text-indigo-100 mb-8">
              {t('about.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/#contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-indigo-600 rounded-full font-semibold hover:bg-gray-100 transition-all"
              >
                {t('about.getStarted')}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-500 text-white rounded-full font-semibold hover:bg-indigo-400 transition-all"
              >
                {t('about.contactUs')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl">ScanIt2Go</span>
              </div>
              <p className="text-gray-400">
                {t('marketing.footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('marketing.footer.product')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/#features" className="hover:text-white transition-colors">{t('marketing.footer.features')}</Link></li>
                <li><Link to="/#pricing" className="hover:text-white transition-colors">{t('marketing.footer.pricing')}</Link></li>
                <li><Link to="/#solutions" className="hover:text-white transition-colors">{t('marketing.footer.solutions')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('marketing.footer.company')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">{t('marketing.footer.aboutUs')}</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">{t('marketing.footer.faq')}</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('marketing.footer.privacyPolicy')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('marketing.footer.contact')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>andy@scanit2go.com</li>
                <li>+44 7829 710 543</li>
                <li>www.scanit2go.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400">&copy; 2026 ScanIt2Go. {t('marketing.footer.allRightsReserved')}</p>
            <div className="flex items-center gap-4 text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Twitter</a>
              <a href="#" className="hover:text-white transition-colors">LinkedIn</a>
              <a href="#" className="hover:text-white transition-colors">Instagram</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
